import React from 'react';
import { Badge, TableCell, TableRow } from '@roger/ui-components';
import type { ImportedMedicationLineResponse } from '../../services/api';
import { formatDateInPT, formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { UnclaimedRecordMarker } from './UnclaimedRecordMarker';
import { IMPORTED_MEDICATION_POPOVER_COPY } from './importedChartLineCopy';

/**
 * One raw Kinnser medication line, rendered inside the main medication list.
 *
 * **Imported-but-true.** The line is not an error and not a draft — it is what
 * the patient's previous chart said, and a nurse on a first visit needs to see
 * it. So it sits in the list with the coded rows rather than in a drawer
 * somebody has to think to open. The amber treatment is what keeps it honest:
 * visibly foreign, visibly not yet Roger's.
 *
 * **What it deliberately does not show.** No administration control and no
 * edit or delete action. There is nothing here to administer — the line has no
 * FDB concept, so the MAR could not record against it — and the only two things
 * a clinician may do to it live in the importer tool, where the raw order
 * string is in front of them while they decide.
 *
 * The columns mirror `MedicationTableRow` exactly. A row that broke alignment
 * would read as a different kind of thing in a list where it must read as the
 * same kind of thing, differently sourced.
 */
const ImportedMedicationRow: React.FC<{
  line: ImportedMedicationLineResponse;
  /** True when the table shows the MAR column, so the row keeps alignment. */
  showMarColumn?: boolean;
  /** True when the table shows the selection column. */
  showSelectionColumn?: boolean;
  variant?: 'row' | 'card';
}> = ({
  line,
  showMarColumn = false,
  showSelectionColumn = false,
  variant = 'row',
}) => {
  // Kinnser's own status word, carried unvalidated. Rendered as given rather
  // than mapped onto Roger's vocabulary: a fourth value from a newer producer
  // must be visible, not silently reclassified.
  const statusBadge = line.status ? (
    <Badge variant='secondary' className='text-xs w-fit capitalize'>
      {line.status}
    </Badge>
  ) : null;

  // The name is what a reader scans for; the order string is what they should
  // actually trust. Both are here, the second in full.
  const details = (
    <div className='min-w-0'>
      <UnclaimedRecordMarker
        popoverLabel='Why this medication is marked as imported'
        lines={[IMPORTED_MEDICATION_POPOVER_COPY]}
        testId={`imported-medication-${line.id}`}
      >
        <span className='font-medium'>
          {line.name || line.title || 'Unnamed imported medication'}
        </span>
      </UnclaimedRecordMarker>
      {line.order_string && (
        <div className='text-sm text-muted-foreground mt-1 break-words'>
          {line.order_string}
        </div>
      )}
      {(line.dose || line.sig) && (
        <div className='text-xs text-muted-foreground mt-0.5'>
          {[line.dose, line.sig].filter(Boolean).join(' — ')}
        </div>
      )}
      <div
        className='text-xs text-muted-foreground mt-1'
        data-testid={`imported-medication-${line.id}-imported-at`}
      >
        Imported {formatDateInPT(line.imported_at)}
      </div>
    </div>
  );

  if (variant === 'card') {
    return (
      <div className='rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2'>
        {/* The drug leads, exactly as it does on a coded card. A clinician
            scanning a stack of cards is looking for a medication name; status
            and dates answer questions they ask only after finding one. */}
        {details}
        <div className='flex flex-wrap items-center gap-2'>
          {statusBadge}
          <span className='text-xs text-muted-foreground'>
            {line.start_date
              ? `Started ${formatDateToMMDDYYYY(line.start_date)}`
              : 'No start date'}
          </span>
          {line.discontinue_date && (
            <span className='text-xs text-muted-foreground'>
              Discontinued {formatDateToMMDDYYYY(line.discontinue_date)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <TableRow className='bg-amber-50/40'>
      {showSelectionColumn && <TableCell />}
      <TableCell>
        <div className='flex flex-col gap-1'>{statusBadge}</div>
      </TableCell>
      <TableCell className='text-sm'>
        {formatDateToMMDDYYYY(line.start_date)}
      </TableCell>
      <TableCell className='text-sm'>
        {formatDateToMMDDYYYY(line.discontinue_date)}
      </TableCell>
      <TableCell>{details}</TableCell>
      {showMarColumn && <TableCell />}
      <TableCell />
    </TableRow>
  );
};

export default ImportedMedicationRow;
